/* Copyright (c) 2020-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ModalConfirm, Text, theme } from "@nordcloud/gnui";
import { BoldText } from "~/components/StepContainer/styles";
import { showError, showSuccess } from "~/services/toast";
import { inflect } from "~/tools";
import { useCloudAccount } from "../../CloudAccountProvider";
import { useDeleteCloudAccount } from "../../hooks/";

type Props = {
  accountsIds: string[];
  isDeleteModalOpen: boolean;
  close: () => void;
  onSuccess?: () => void;
};

export function DeleteCloudAccount({
  accountsIds,
  isDeleteModalOpen,
  close,
  onSuccess,
}: Readonly<Props>) {
  const [deleteCloudAccount] = useDeleteCloudAccount({
    onSuccess: () => {
      close();
      onSuccess?.();
    },
  });
  const { cleanRowSelection } = useCloudAccount();

  function deleteAccounts(ids: string[]) {
    if (ids.length === 1) {
      return deleteCloudAccount(ids[0])
        .then(() => showSuccess(`Cloud account has been removed.`))
        .catch(() => showError("Something went wrong."));
    }

    return Promise.all(ids.map((id) => deleteCloudAccount(id)))
      .then(() => showSuccess(`${ids.length} cloud accounts has been removed.`))
      .catch(() => showError("Something went wrong."));
  }

  function onDelete(ids: string[]) {
    deleteAccounts(ids).finally(() => cleanRowSelection?.());
  }

  const accountText = inflect("Account")(accountsIds.length);

  return (
    <ModalConfirm
      onClose={close}
      isOpen={isDeleteModalOpen}
      confirm={() => onDelete(accountsIds)}
      actionLabel="Delete"
      contentLabel={`Delete Cloud ${inflect("Account")(accountsIds.length)}`}
    >
      <Text>Are you sure you want to delete Cloud {accountText}?</Text>
      <Text color={theme.color.text.warning}>
        Please be advised that this action will{" "}
        <BoldText>remove all resources</BoldText> associated with Cloud{" "}
        {accountText} from the system.
      </Text>
    </ModalConfirm>
  );
}
